import React, { useState, useEffect, useRef } from "react";
import { Link } from "gatsby";
import DigiryteLogo from "../../assets/Images/logo/digiryte.svg";
import { PRACTICES as PRACTICES_DATA } from "../../data/practices";
import "./ModernNav.scss";

const ArrowIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

const ChevronDown = ({ open }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transition: "transform 0.25s ease",
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
      marginLeft: 4,
      verticalAlign: "middle",
    }}
  >
    <path d="M2 4l4 4 4-4" />
  </svg>
);

const INSIGHTS_LINKS = [
  { to: "/insights/articles", label: "Articles" },
  { to: "/insights/white-papers", label: "White Papers" },
  { to: "/insights/podcast", label: "Podcast" },
];

const SERVICES_ENGAGEMENT = [
  {
    to: "/services/consult",
    label: "Consult",
    desc:
      "AI transformation strategy and technology direction for leadership teams.",
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M12 16v-4"></path>
        <path d="M12 8h.01"></path>
      </svg>
    ),
  },
  {
    to: "/services/audit",
    label: "Audit",
    desc: "Independent architecture, risk and compliance assessment.",
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
      </svg>
    ),
  },
  {
    to: "/services/build",
    label: "Build",
    desc:
      "AI-powered platforms and digital products engineered for production.",
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
        <polyline points="2 17 12 22 22 17"></polyline>
        <polyline points="2 12 12 17 22 12"></polyline>
      </svg>
    ),
  },
  {
    to: "/services",
    label: "Scale",
    desc: "Senior-led engineering pods and platform evolution programmes.",
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
        <polyline points="17 6 23 6 23 12"></polyline>
      </svg>
    ),
  },
];

const PRACTICES_ICONS = {
  "/ventureryte": (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
    </svg>
  ),
  "/growryte": (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
    </svg>
  ),
};

const PRACTICES = PRACTICES_DATA.map((p) => ({
  ...p,
  icon: PRACTICES_ICONS[p.to],
}));

const SERVICES_EXPERTISE = [
  { to: "/mvp-development", label: "MVP Development" },
  { to: "/data-engineering", label: "Data Engineering" },
  { to: "/machine-learning", label: "AI / ML" },
  { to: "/product-scaling", label: "Product Scaling" },
  {
    to: "/mobile-application-development",
    label: "Mobile Application Development",
  },
  { to: "/web-application-development", label: "Web Application Development" },
  { to: "/full-product-development", label: "Full Product Development" },
  {
    to: "/product-designing-and-prototyping",
    label: "Product Designing & Prototyping",
  },
  { to: "/code-ux-audit", label: "Code & UX Audit" },
  { to: "/digital-transformation", label: "Digital Transformation" },
];

const ModernNav = ({ isIndexPage = false }) => {
  const [scrolled, setScrolled] = useState(!isIndexPage);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hoverServices, setHoverServices] = useState(false);
  const [hoverInsights, setHoverInsights] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileInsightsOpen, setMobileInsightsOpen] = useState(false);
  const closeTimerRef = useRef(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // A gap sits between the nav link and the dropdown below it, so a
  // direct mouseleave would close the menu while the pointer is still
  // crossing that gap. Delaying the close gives it time to land inside.
  const scheduleClose = (setter) => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setter(false), 250);
  };

  useEffect(() => clearCloseTimer, []);

  useEffect(() => {
    let isScrolled = !isIndexPage || window.scrollY > 40;
    const onScroll = () => {
      const next = !isIndexPage || window.scrollY > 40;
      if (next !== isScrolled) {
        isScrolled = next;
        setScrolled(next);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isIndexPage]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <>
      <nav
        className={`mnav${scrolled || !isIndexPage ? " mnav--scrolled" : ""}${
          drawerOpen ? " mnav--open" : ""
        }`}
      >
        <div className="mnav__inner">
          {/* Logo */}
          <Link to="/" className="mnav__logo">
            <img src={DigiryteLogo} alt="digiRyte" className="mnav__logo-img" />
          </Link>

          {/* Desktop links */}
          <ul className="mnav__links">
            {/* Services with mega-menu */}
            <li
              className="mnav__has-dropdown"
              onMouseEnter={() => {
                clearCloseTimer();
                setHoverServices(true);
                setHoverInsights(false);
              }}
              onMouseLeave={() => scheduleClose(setHoverServices)}
            >
              <Link
                to="/services"
                className={hoverServices ? "mnav__link--active" : ""}
              >
                Services <ChevronDown open={hoverServices} />
              </Link>
              <div
                className={`mnav__dropdown mnav__dropdown--mega${
                  hoverServices ? " mnav__dropdown--visible" : ""
                }`}
                onMouseEnter={clearCloseTimer}
                onMouseLeave={() => scheduleClose(setHoverServices)}
              >
                <div className="mnav__mega-inner">
                  <div className="mnav__mega-col mnav__mega-col--services">
                    <p className="mnav__mega-heading">Services</p>
                    <div className="mnav__mega-grid">
                      {SERVICES_ENGAGEMENT.map((s) => (
                        <Link key={s.to} to={s.to} className="mnav__mega-item">
                          <div className="mnav__mega-item__icon">{s.icon}</div>
                          <div className="mnav__mega-item__content">
                            <strong>{s.label}</strong>
                            <span>{s.desc}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="mnav__mega-divider" />
                  <div className="mnav__mega-col mnav__mega-col--practices">
                    <p className="mnav__mega-heading">Practices</p>
                    <div className="mnav__mega-practices">
                      {PRACTICES.map((p) => (
                        <Link
                          key={p.to}
                          to={p.to}
                          className="mnav__mega-practice-item"
                        >
                          <div className="mnav__mega-practice-item__icon">
                            {p.icon}
                          </div>
                          <div className="mnav__mega-practice-item__content">
                            <strong>{p.label}</strong>
                            <span>{p.desc}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="mnav__mega-divider" />
                  <div className="mnav__mega-col mnav__mega-col--expertise">
                    <p className="mnav__mega-heading">Expertise</p>
                    <ul className="mnav__mega-list">
                      {SERVICES_EXPERTISE.map((s) => (
                        <li key={s.to}>
                          <Link to={s.to}>{s.label}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </li>

            <li>
              <Link to="/clients">Clients</Link>
            </li>

            {/* Insights with dropdown */}
            <li
              className="mnav__has-dropdown"
              onMouseEnter={() => {
                clearCloseTimer();
                setHoverInsights(true);
                setHoverServices(false);
              }}
              onMouseLeave={() => scheduleClose(setHoverInsights)}
            >
              <Link
                to="/insights"
                className={hoverInsights ? "mnav__link--active" : ""}
              >
                Insights <ChevronDown open={hoverInsights} />
              </Link>
              <div
                className={`mnav__dropdown${
                  hoverInsights ? " mnav__dropdown--visible" : ""
                }`}
                onMouseEnter={clearCloseTimer}
                onMouseLeave={() => scheduleClose(setHoverInsights)}
              >
                <div className="mnav__dropdown-inner">
                  {INSIGHTS_LINKS.map((l) => (
                    <Link key={l.to} to={l.to} className="mnav__dropdown-item">
                      {l.label}
                    </Link>
                  ))}
                </div>
              </div>
            </li>

            <li>
              <Link to="/about-us">About Us</Link>
            </li>
          </ul>

          {/* Desktop CTA */}
          <Link to="/contact-us" className="mnav__cta">
            Get in Touch <ArrowIcon />
          </Link>

          {/* Mobile hamburger */}
          <button
            className={`mnav__burger${
              drawerOpen ? " mnav__burger--active" : ""
            }`}
            onClick={() => setDrawerOpen(!drawerOpen)}
            aria-label="Toggle menu"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>

      {/* Mobile overlay */}
      <div
        className={`mnav-drawer-overlay${
          drawerOpen ? " mnav-drawer-overlay--visible" : ""
        }`}
        onClick={closeDrawer}
      />

      {/* Mobile drawer */}
      <aside className={`mnav-drawer${drawerOpen ? " mnav-drawer--open" : ""}`}>
        <ul className="mnav-drawer__links">
          {/* Mobile Services accordion */}
          <li className="mnav-drawer__has-expand">
            <button
              className="mnav-drawer__expand-btn"
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
            >
              Services <ChevronDown open={mobileServicesOpen} />
            </button>
            {mobileServicesOpen && (
              <ul className="mnav-drawer__sub">
                <li className="mnav-drawer__sub-heading">Services</li>
                {SERVICES_ENGAGEMENT.map((s) => (
                  <li key={s.to}>
                    <Link to={s.to} onClick={closeDrawer}>
                      {s.label}
                    </Link>
                  </li>
                ))}
                <li className="mnav-drawer__sub-heading">Expertise</li>
                {SERVICES_EXPERTISE.map((s) => (
                  <li key={s.to}>
                    <Link to={s.to} onClick={closeDrawer}>
                      {s.label}
                    </Link>
                  </li>
                ))}
                <li className="mnav-drawer__sub-heading">Practices</li>
                {PRACTICES.map((p) => (
                  <li key={p.to} className="mnav-drawer__sub-item--practices">
                    <Link to={p.to} onClick={closeDrawer}>
                      {p.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>

          <li>
            <Link to="/clients" onClick={closeDrawer}>
              Clients
            </Link>
          </li>

          {/* Mobile Insights accordion */}
          <li className="mnav-drawer__has-expand">
            <button
              className="mnav-drawer__expand-btn"
              onClick={() => setMobileInsightsOpen(!mobileInsightsOpen)}
            >
              Insights <ChevronDown open={mobileInsightsOpen} />
            </button>
            {mobileInsightsOpen && (
              <ul className="mnav-drawer__sub">
                {INSIGHTS_LINKS.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} onClick={closeDrawer}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>

          <li>
            <Link to="/about-us" onClick={closeDrawer}>
              About Us
            </Link>
          </li>
        </ul>

        <Link
          to="/contact-us"
          className="mnav-drawer__cta"
          onClick={closeDrawer}
        >
          Get in Touch <ArrowIcon />
        </Link>
      </aside>
    </>
  );
};

export default ModernNav;
